import React, { useContext, useEffect, useReducer, useState } from "react";
import { toast } from "react-toastify";
import {
  Avatar,
  Button,
  Chip,
  FormControl,
  IconButton,
  InputAdornment,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Switch,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import SearchIcon from "@material-ui/icons/Search";
import DeleteOutlineIcon from "@material-ui/icons/DeleteOutline";
import EditIcon from "@material-ui/icons/Edit";
import MainContainer from "../../components/MainContainer";
import MainHeader from "../../components/MainHeader";
import MainHeaderButtonsWrapper from "../../components/MainHeaderButtonsWrapper";
import Title from "../../components/Title";
import TaskModal from "../../components/TaskModal";
import ConfirmationModal from "../../components/ConfirmationModal";
import TableRowSkeleton from "../../components/TableRowSkeleton";
import api from "../../services/api";
import openSocket from "../../services/socket-io";
import toastError from "../../errors/toastError";
import { AuthContext } from "../../context/Auth/AuthContext";

const reducer = (state, action) => {
  if (action.type === "LOAD_TASKS") {
    return action.payload;
  }

  if (action.type === "UPSERT_TASK") {
    const task = action.payload;
    const taskIndex = state.findIndex(item => item.id === task.id);

    if (taskIndex !== -1) {
      state[taskIndex] = task;
      return [...state];
    }

    return [task, ...state];
  }

  if (action.type === "DELETE_TASK") {
    return state.filter(task => task.id !== action.payload);
  }

  return state;
};

const useStyles = makeStyles(theme => ({
  mainPaper: {
    flex: 1,
    padding: theme.spacing(1),
    overflowY: "scroll",
    ...theme.scrollbarStyles
  },
  filters: {
    display: "flex",
    gap: theme.spacing(1),
    alignItems: "center"
  },
  taskTitleCell: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(1)
  },
  overdueRow: {
    backgroundColor: "rgba(255, 152, 0, 0.08)"
  },
  statusSelect: {
    minWidth: 140
  }
}));

const statusOptions = [
  { value: "", label: "Todos os status" },
  { value: "pending", label: "Pendente" },
  { value: "in_progress", label: "Em andamento" },
  { value: "done", label: "Concluida" }
];

const statusLabels = {
  pending: "Pendente",
  in_progress: "Em andamento",
  done: "Concluida"
};

const getUserDisplayName = user =>
  [user?.firstName, user?.lastName].filter(Boolean).join(" ").trim() ||
  user?.name ||
  "-";

const formatDate = value => {
  if (!value) {
    return "-";
  }

  return new Date(value).toLocaleString("pt-BR");
};

const Tasks = () => {
  const classes = useStyles();
  const { user } = useContext(AuthContext);
  const [tasks, dispatch] = useReducer(reducer, []);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(false);
  const [taskModalOpen, setTaskModalOpen] = useState(false);
  const [selectedTask, setSelectedTask] = useState(null);
  const [confirmModalOpen, setConfirmModalOpen] = useState(false);
  const [deletingTask, setDeletingTask] = useState(null);
  const [searchParam, setSearchParam] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [showAll, setShowAll] = useState(user.profile === "admin");

  useEffect(() => {
    const fetchUsers = async () => {
      try {
        const { data } = await api.get("/users", {
          params: { pageNumber: "all" }
        });
        setUsers(data.users || []);
      } catch (err) {
        toastError(err);
      }
    };

    fetchUsers();
  }, []);

  useEffect(() => {
    const fetchTasks = async () => {
      setLoading(true);

      try {
        const { data } = await api.get("/tasks", {
          params: {
            searchParam,
            status: statusFilter,
            showAll
          }
        });

        dispatch({ type: "LOAD_TASKS", payload: data });
      } catch (err) {
        toastError(err);
      } finally {
        setLoading(false);
      }
    };

    fetchTasks();
  }, [searchParam, statusFilter, showAll]);

  useEffect(() => {
    const socket = openSocket();

    socket.on("userTask", data => {
      if (data.action === "delete") {
        dispatch({ type: "DELETE_TASK", payload: data.taskId });
        return;
      }

      if (data.task) {
        dispatch({ type: "UPSERT_TASK", payload: data.task });
      }
    });

    return () => {
      socket.disconnect();
    };
  }, []);

  const handleOpenModal = task => {
    setSelectedTask(task || null);
    setTaskModalOpen(true);
  };

  const handleCloseModal = () => {
    setSelectedTask(null);
    setTaskModalOpen(false);
  };

  const handleDeleteTask = async () => {
    try {
      await api.delete(`/tasks/${deletingTask.id}`);
      toast.success("Tarefa removida.");
    } catch (err) {
      toastError(err);
    } finally {
      setConfirmModalOpen(false);
      setDeletingTask(null);
    }
  };

  const handleStatusChange = async (task, newStatus) => {
    try {
      await api.put(`/tasks/${task.id}`, { status: newStatus });
    } catch (err) {
      toastError(err);
    }
  };

  const filteredTasks = tasks.filter(task => {
    if (statusFilter && task.status !== statusFilter) {
      return false;
    }

    if (!searchParam) {
      return true;
    }

    const haystack = `${task.title} ${task.description || ""}`.toLowerCase();
    return haystack.includes(searchParam.toLowerCase());
  });

  return (
    <MainContainer>
      <ConfirmationModal
        title={deletingTask ? `Excluir tarefa "${deletingTask.title}"?` : ""}
        open={confirmModalOpen}
        onClose={setConfirmModalOpen}
        onConfirm={handleDeleteTask}
      >
        Essa acao remove a tarefa e o historico de notificacao ligado a ela.
      </ConfirmationModal>

      <TaskModal
        open={taskModalOpen}
        onClose={handleCloseModal}
        task={selectedTask}
        users={users}
        currentUser={user}
      />

      <MainHeader>
        <Title>Tarefas</Title>
        <MainHeaderButtonsWrapper>
          <div className={classes.filters}>
            <TextField
              placeholder="Buscar tarefa"
              type="search"
              value={searchParam}
              onChange={event => setSearchParam(event.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon style={{ color: "gray" }} />
                  </InputAdornment>
                )
              }}
            />
            <FormControl variant="outlined" size="small">
              <InputLabel id="task-status-filter-label">Status</InputLabel>
              <Select
                labelId="task-status-filter-label"
                value={statusFilter}
                onChange={event => setStatusFilter(event.target.value)}
                label="Status"
              >
                {statusOptions.map(option => (
                  <MenuItem key={option.value || "all"} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
            {user.profile === "admin" && (
              <div>
                <Switch
                  checked={showAll}
                  onChange={() => setShowAll(prevState => !prevState)}
                  color="primary"
                />
                Mostrar todas
              </div>
            )}
          </div>
          <Button
            variant="contained"
            color="primary"
            onClick={() => handleOpenModal(null)}
          >
            Nova tarefa
          </Button>
        </MainHeaderButtonsWrapper>
      </MainHeader>

      <Paper className={classes.mainPaper} variant="outlined">
        <Table size="small">
          <TableHead>
            <TableRow>
              <TableCell>Titulo</TableCell>
              <TableCell>Responsavel</TableCell>
              <TableCell>Quem passou</TableCell>
              <TableCell>Prazo</TableCell>
              <TableCell>Status</TableCell>
              <TableCell align="center">Acoes</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {filteredTasks.map(task => {
              const isOverdue =
                task.status !== "done" && new Date(task.dueDate) < new Date();

              return (
                <TableRow key={task.id} className={isOverdue ? classes.overdueRow : ""}>
                  <TableCell>
                    <div className={classes.taskTitleCell}>
                      <Avatar src={task.assignedToUser?.profileImageUrl}>
                        {getUserDisplayName(task.assignedToUser).charAt(0)}
                      </Avatar>
                      <div>
                        <div>{task.title}</div>
                        <small>{task.description || "Sem descricao"}</small>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell>{getUserDisplayName(task.assignedToUser)}</TableCell>
                  <TableCell>{getUserDisplayName(task.createdByUser)}</TableCell>
                  <TableCell>
                    <Chip
                      label={formatDate(task.dueDate)}
                      color={isOverdue ? "secondary" : "default"}
                      size="small"
                    />
                  </TableCell>
                  <TableCell>
                    <FormControl
                      variant="outlined"
                      size="small"
                      className={classes.statusSelect}
                    >
                      <Select
                        value={task.status}
                        onChange={event =>
                          handleStatusChange(task, event.target.value)
                        }
                      >
                        {statusOptions
                          .filter(option => option.value)
                          .map(option => (
                            <MenuItem key={option.value} value={option.value}>
                              {statusLabels[option.value]}
                            </MenuItem>
                          ))}
                      </Select>
                    </FormControl>
                  </TableCell>
                  <TableCell align="center">
                    <IconButton size="small" onClick={() => handleOpenModal(task)}>
                      <EditIcon />
                    </IconButton>
                    <IconButton
                      size="small"
                      onClick={() => {
                        setDeletingTask(task);
                        setConfirmModalOpen(true);
                      }}
                    >
                      <DeleteOutlineIcon />
                    </IconButton>
                  </TableCell>
                </TableRow>
              );
            })}
            {loading && <TableRowSkeleton columns={6} />}
          </TableBody>
        </Table>
      </Paper>
    </MainContainer>
  );
};

export default Tasks;
